import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import type { ApiError } from '@/shared/api/errors';
import { type Guideline, guidelineKeys, guidelinesApi } from '@/shared/triage/guidelines';

import { type EmailListParams, emailKeys, emailsApi } from './api';
import type { AttachmentUrl, EmailDetail, ReadAllResult } from './types';

/**
 * How often the open inbox asks again. One clock for the list and for the counts, so the
 * filter cannot say «Pendientes 1» over a list that is still empty.
 */
export const INBOX_POLL_MS = 60_000;

/**
 * Only the first page polls: that is where new mail lands. On a later page every new
 * arrival pushes the rows down by one, and the page would shift under the reader's eyes.
 */
export function listPollInterval(params: EmailListParams): number | false {
  return params.offset ? false : INBOX_POLL_MS;
}

export function useEmailList(params: EmailListParams) {
  return useQuery({
    queryKey: emailKeys.list(params),
    queryFn: () => emailsApi.list(params),
    // The previous page stays on screen while the next one loads. Without it `data` is
    // undefined on every page change: the list blinks out and the pager with it, and a
    // page that fails leaves no way back to the previous one.
    placeholderData: keepPreviousData,
    refetchInterval: listPollInterval(params),
  });
}

/**
 * The rail of the inbox (contract v10.25): each view and each folder with its total and
 * what the person has not opened, in one request, on the inbox's clock. Replaces the six
 * `limit=1` lists the status filter used to ask for its numbers.
 */
export function useInboxViews(mailbox: string) {
  return useQuery({
    queryKey: emailKeys.views(mailbox),
    queryFn: () => emailsApi.views(mailbox),
    refetchInterval: INBOX_POLL_MS,
    placeholderData: keepPreviousData,
  });
}

/**
 * The email above and below this one in the list it was opened from (contract v10.25).
 * Asked when the detail opens and again after every move of this email, since a move
 * changes the list it sits in.
 */
export function useNeighbours(emailId: string, params: EmailListParams) {
  return useQuery({
    queryKey: emailKeys.neighbours(emailId, params),
    queryFn: () => emailsApi.neighbours(emailId, params),
  });
}

/**
 * How many rows «No leídos» would leave with these filters (contract v10.22): the
 * `total` of the list with `read=false` and `limit=1`, on the inbox's clock like the
 * other counts. Undefined while it loads.
 */
export function useUnreadCount(filters: Omit<EmailListParams, 'read' | 'limit' | 'offset'>) {
  const params: EmailListParams = { ...filters, read: false, limit: 1, offset: 0 };
  const query = useQuery({
    queryKey: emailKeys.list(params),
    queryFn: () => emailsApi.list(params),
    refetchInterval: INBOX_POLL_MS,
    placeholderData: keepPreviousData,
  });
  return query.data?.total;
}

/**
 * The mailboxes of the filter, on the inbox's clock like the list. `GET /api/emails/mailboxes`
 * only names a mailbox once it has mail, so a mailbox registered while the inbox is open
 * turns up with its first email, not when it was registered: with the five minutes of
 * `staleTime` it had before and no polling, `ventas@` stayed out of the filter (and the
 * «Buzón» column hidden) for as long as the page stayed open, with its mail already in
 * the list (production, 2026-09-24).
 */
export function useMailboxes() {
  return useQuery({
    queryKey: emailKeys.mailboxes,
    queryFn: emailsApi.mailboxes,
    refetchInterval: INBOX_POLL_MS,
  });
}

/**
 * Heartbeats per mailbox, refreshed every minute while the inbox is open: the strip that
 * says whether the reader is alive. The counts on the filter are `useEmailCounts`.
 */
export function useIngestStats() {
  return useQuery({
    queryKey: emailKeys.ingest,
    queryFn: emailsApi.ingest,
    staleTime: 30_000,
    refetchInterval: INBOX_POLL_MS,
  });
}

export { useEmailDetail, useReprocess, useRescue } from '@/shared/emails/useEmails';

/**
 * Fetch a signed link and open it. A mutation, not a query: every click asks for a fresh
 * link because they expire, and nothing about them belongs in the cache.
 *
 * The `window.open` happens after an `await`, so the browser no longer sees it as the
 * direct consequence of the click: Safari blocks it always and Chrome blocks it when the
 * request is slow. A blocked open returns `null`, and then the link is kept in
 * `blockedLink` so the screen can paint an `<a>` the user clicks himself.
 *
 * The features string must NOT carry `noopener`: with it the spec makes `window.open`
 * return `null` even when the window did open, so every download would be reported as
 * blocked. Nulling `opener` on the returned proxy gives the same protection and keeps
 * the return value meaningful.
 */
export function useDownloadAttachment(emailId: string) {
  const [blockedLink, setBlockedLink] = useState<AttachmentUrl | null>(null);
  const mutation = useMutation<AttachmentUrl, ApiError, string>({
    mutationFn: (attachmentId) => emailsApi.attachmentUrl(emailId, attachmentId),
    onMutate: () => {
      setBlockedLink(null);
    },
    onSuccess: (link) => {
      const opened = window.open(link.url, '_blank');
      if (opened) {
        opened.opener = null;
      } else {
        setBlockedLink(link);
      }
    },
  });
  return { mutation, blockedLink };
}

/** Confirm or correct the live decision: shared since contract v10.24 (the quote editor
 * corrects too), re-exported so the inbox keeps importing it from here. */
export { useCorrectTriage } from '@/shared/emails/useEmails';

/**
 * Read and unread (contract v10.22). The detail in the cache takes the new state at once,
 * and every list is asked again: the bold row, the «No leídos» count and that filter's
 * rows all come from the lists.
 */
function useReadState(emailId: string, read: boolean) {
  const queryClient = useQueryClient();
  return useMutation<void, ApiError, void>({
    mutationFn: () => (read ? emailsApi.markRead(emailId) : emailsApi.markUnread(emailId)),
    onSuccess: () => {
      queryClient.setQueryData<EmailDetail>(emailKeys.detail(emailId), (previous) =>
        previous ? { ...previous, is_read: read } : previous,
      );
      void queryClient.invalidateQueries({ queryKey: emailKeys.lists });
    },
  });
}

export function useMarkRead(emailId: string) {
  return useReadState(emailId, true);
}

export function useMarkUnread(emailId: string) {
  return useReadState(emailId, false);
}

/** «Marcar todo como leído» with the filters of the list on screen, every page. */
export function useReadAll() {
  const queryClient = useQueryClient();
  return useMutation<ReadAllResult, ApiError, EmailListParams>({
    mutationFn: (filters) => emailsApi.readAll(filters),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: emailKeys.all });
    },
  });
}

/**
 * «Deshacer» under what a note taught (contract v10.35): the criterion goes off from the
 * next email on. The detail is read again for its `note_learning` (now `desactivado`), and
 * «Lo que ha aprendido» of Administración, if it is in the cache, with it.
 */
export function useUndoLearning(emailId: string) {
  const queryClient = useQueryClient();
  return useMutation<Guideline, ApiError, string>({
    mutationFn: (guidelineId) => guidelinesApi.update(guidelineId, { status: 'desactivado' }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: emailKeys.detail(emailId) });
      void queryClient.invalidateQueries({ queryKey: guidelineKeys.all });
    },
  });
}

type Move = 'trash' | 'untrash' | 'returnToInbox';

/**
 * «Eliminar», «Recuperar» and «Devolver a la bandeja» (contract v10.25). The API moves
 * the message in the mailbox there and then and answers with the email as it is now, so
 * the detail takes the answer; the lists, the rail and the neighbours are asked again,
 * because the email just changed view.
 */
export function useMoveEmail(emailId: string, move: Move) {
  const queryClient = useQueryClient();
  return useMutation<EmailDetail, ApiError, void>({
    mutationFn: () => emailsApi[move](emailId),
    onSuccess: (detail) => {
      queryClient.setQueryData(emailKeys.detail(emailId), detail);
      void queryClient.invalidateQueries({ queryKey: emailKeys.lists });
      void queryClient.invalidateQueries({ queryKey: emailKeys.allViews });
      void queryClient.invalidateQueries({ queryKey: emailKeys.allNeighbours });
    },
  });
}
